// Dashboard — the home screen: today's ledger, birthdays, goal bars.
import { useState, useMemo } from "react";
import { clientToday, clientMonth, clientYear, addDays } from "../domain";
import { sl, cl, getCurrencySymbol, clientPhone, openWhatsApp } from "../lib/format";
import { themeTokens } from "../lib/theme";
import { usePackageStates } from "../lib/packages";
import { Icon, useToast } from "../ui/primitives";

export const TodaysLedger=({theme,cb,tc,mc,rows})=>{
  const isDark=theme?.dark;
  const borderC=isDark?"#334155":"#e5e7eb";
  return(
    <div className="rounded-2xl shadow-sm border overflow-hidden" style={{background:cb,borderColor:borderC}}>
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b" style={{borderColor:borderC,borderStyle:"dashed"}}>
        <p className="text-sm font-black" style={{fontFamily:"'Playfair Display',serif",color:tc}}>Today's Ledger</p>
        <p className="text-xs" style={{color:mc,fontVariantNumeric:"tabular-nums"}}>
          {new Date().toLocaleDateString("en-IN",{weekday:"short",day:"numeric",month:"short"}).toUpperCase()}
        </p>
      </div>
      <div className="px-5 py-1">
        {rows.map((r,i)=>(
          <div key={i}
            className={`flex items-center justify-between py-3 ${r.onClick?"cursor-pointer active:opacity-70":""} ${i>0?"border-t":""}`}
            style={{borderColor:borderC,borderStyle:"dotted"}}
            onClick={r.onClick}>
            <span className="flex items-center gap-2.5 text-sm" style={{color:tc}}>
              <span className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{background:r.iconColor+"18",color:r.iconColor}}>
                <Icon name={r.icon} size={13}/>
              </span>
              {r.label}
            </span>
            <span className="text-sm font-bold" style={{color:r.valueColor||tc,fontVariantNumeric:"tabular-nums"}}>{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Dashboard=({user,clients,appointments,payments=[],theme,setPage,onOpenReports,onOpenProfile,userId,monthlyGoal,yearlyGoal,profile,attendanceRecords})=>{
  const toast=useToast();
  const [showReminders,setShowReminders]=useState(false);
  const [reminderTab,setReminderTab]=useState("class");
  const [showProfileBanner,setShowProfileBanner]=useState(true);

  // ── PROFILE COMPLETION SCORE ──────────────────────────────────────────────
  const calcProfileCompletion=(p)=>{
    if(!p)return 0;
    let score=0;
    if(p.name?.trim())score+=20;
    if(p.mobile?.trim()||p.phone?.trim())score+=15;
    if(p.profession?.trim())score+=10;
    if(p.businessName?.trim())score+=15;
    if(p.email?.trim())score+=10;
    if(p.city?.trim())score+=10;
    if(p.services?.length>0)score+=15;
    if(p.currency&&p.currency!=="₹")score+=5; // gave it thought
    else if(p.currency)score+=5;
    return Math.min(score,100);
  };
  const completion=calcProfileCompletion(profile);
  const showBanner=showProfileBanner&&completion<80&&profile?.quickSetup;

  const psOf=usePackageStates(clients);

  const tot=clients.length;
  const act=clients.filter(c=>c.classesRemaining>0||c.package?.type!=="custom").length;
  const due=clients.filter(c=>psOf(c).amountDue>0).length;
  const dueAmount=clients.reduce((s,c)=>s+psOf(c).amountDue,0);
  const todApts=appointments.filter(a=>a.date===clientToday()&&a.status==="Upcoming").length;
  const weekAgo=Date.now()-7*24*60*60*1000;
  const collectedWeek=payments.filter(p=>p.date&&new Date(p.date).getTime()>=weekAgo).reduce((s,p)=>s+(p.amount||0),0);
  const currency=getCurrencySymbol(profile)||"₹";
  const {isDark,tc,mc,bc}=themeTokens(theme);
  const cb=isDark?"#1e293b":"#fff";
  const hr=new Date().getHours();
  const greet=hr<12?"Good morning":hr<17?"Good afternoon":"Good evening";

  // Smart reminders
  const soonStr=addDays(clientToday(),3);
  const expiring=clients.filter(c=>{
    const ps=psOf(c);
    if(ps.isSessionBased&&ps.classesRemaining!=null&&ps.classesRemaining>=1&&ps.classesRemaining<=2)return true;
    return !!ps.expiryDate&&ps.expiryDate>=clientToday()&&ps.expiryDate<=soonStr;
  });
  const overdue=clients.filter(c=>{
    if(psOf(c).amountDue<=0)return false;
    const lastPay=payments.filter(p=>p.clientId===c.id).sort((a,b)=>b.date?.localeCompare(a.date))[0];
    if(!lastPay)return true;
    const days=(new Date()-new Date(lastPay.date))/(1000*60*60*24);
    return days>30;
  });

  // Birthday reminders — this week
  const todayStr=clientToday();
  const weekMmDds=Array.from({length:7},(_,i)=>{const d=new Date();d.setDate(d.getDate()+i);return clientToday(d).slice(5);});
  const birthdays=clients.filter(c=>{
    if(!c.dob)return false;
    const mmdd=c.dob.slice(5);
    return weekMmDds.includes(mmdd);
  });
  // days until this client's birthday — index into weekMmDds, which is built by
  // walking real dates so the year boundary is already handled. 0..6 for any
  // client in `birthdays` (that list is filtered against the same array).
  const bdayDaysAway=(c)=>weekMmDds.indexOf((c?.dob||"").slice(5));
  const bdayLabel=(c)=>{const d=bdayDaysAway(c);return d===0?"🎂 Birthday today!":d===1?"🎂 Birthday tomorrow":`🎂 Birthday in ${d} days`;};
  const birthdayToday=birthdays.filter(c=>bdayDaysAway(c)===0);

  // Revenue chart — last 6 months. Recomputed when payments change or the
  // trailing window itself shifts (clientMonth() ticks over) — not on every
  // render (a toast, a banner dismissal — anything unrelated).
  const rev=useMemo(()=>{
    const months=[];
    for(let i=5;i>=0;i--){
      const d=new Date();d.setMonth(d.getMonth()-i);
      const key=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      const label=d.toLocaleDateString("en-IN",{month:"short"});
      const collected=payments.filter(p=>p.date?.startsWith(key)).reduce((s,p)=>s+(p.amount||0),0);
      months.push({label,collected,key});
    }
    return months;
  },[payments,clientMonth()]); // eslint-disable-line react-hooks/exhaustive-deps
  const maxRev=Math.max(...rev.map(r=>r.collected),1);

  // Goal progress
  const thisMonth=clientMonth();
  const thisYear=clientYear();
  const collectedMonth=payments.filter(p=>p.date?.startsWith(thisMonth)).reduce((s,p)=>s+(p.amount||0),0);
  const billedPending=dueAmount; // outstanding across all clients — the GoalBar "billed" marker
  const collectedYear=payments.filter(p=>p.date?.startsWith(thisYear)).reduce((s,p)=>s+(p.amount||0),0);

  const GoalBar=({label,collected,billed,goal,color})=>{
    if(!goal)return null;
    const pct=Math.min((collected/goal)*100,100);
    const bpct=Math.min((billed/goal)*100,100);
    const barColor=pct>=100?"#16a34a":pct>=50?"#d97706":"#ef4444";
    return(
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold" style={{color:tc}}>{label}</p>
          <p className="text-xs" style={{color:mc}}>₹{collected.toLocaleString("en-IN")} <span style={{color:mc}}>of ₹{goal.toLocaleString("en-IN")}</span></p>
        </div>
        <div className="relative h-3 rounded-full overflow-hidden" style={{background:isDark?"#334155":"#f3f4f6"}}>
          {/* Billed (pending) bar — lighter */}
          <div className="absolute left-0 top-0 h-full rounded-full transition-all" style={{width:`${bpct}%`,background:`${barColor}33`}}/>
          {/* Collected bar */}
          <div className="absolute left-0 top-0 h-full rounded-full transition-all" style={{width:`${pct}%`,background:barColor}}/>
        </div>
        <div className="flex justify-between text-xs" style={{color:mc}}>
          <span style={{color:barColor,fontWeight:600}}>{Math.round(pct)}% collected</span>
          {billed>collected&&<span>+₹{(billed-collected).toLocaleString("en-IN")} billed pending</span>}
        </div>
      </div>
    );
  };

  const waMsg=(c,type)=>{
    if(type==="expiring")return`Hi ${c.name}! Your package has only ${c.classesRemaining} ${c.classesRemaining===1?sl(profile,1):sl(profile,2)} remaining. Renew today to keep going! 💪`;
    if(type==="overdue")return`Hi ${c.name}! This is a gentle reminder that your payment is pending. Please clear it at your earliest convenience. Thank you!`;
    if(type==="birthday")return`Happy Birthday ${c.name}! 🎂🎉 Wishing you a wonderful day filled with joy and energy! See you at our next ${sl(profile,1).toLowerCase()}!`;
    return"";
  };
  const openWa=(c,type)=>{
    if(!openWhatsApp(clientPhone(c),waMsg(c,type)))toast(`No phone number saved for ${c.name}`,"warning");
  };

  return(
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-black" style={{fontFamily:"'Playfair Display',serif",color:tc}}>{greet}, {(profile?.name||user?.displayName||"").split(" ")[0]||"there"} 👋</h1>
        <p className="text-sm mt-1" style={{color:mc}}>{new Date().toLocaleDateString("en-IN",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}</p>
      </div>

      {/* Profile completion banner */}
      {showBanner&&(
        <div className="rounded-2xl p-4 border" style={{background:isDark?"#0f172a":"#f0fdf4",borderColor:"#86efac"}}>
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <span style={{fontSize:"20px"}}>👤</span>
              <div>
                <p className="font-bold text-sm" style={{color:"#15803d"}}>Complete your profile</p>
                <p className="text-xs" style={{color:"#16a34a"}}>Unlock receipts, invoices & more</p>
              </div>
            </div>
            <button onClick={()=>setShowProfileBanner(false)} style={{color:"#86efac",fontSize:"16px",lineHeight:1,flexShrink:0}}>✕</button>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-2 rounded-full" style={{background:"#bbf7d0"}}>
              <div className="h-full rounded-full transition-all" style={{width:`${completion}%`,background:"#16a34a"}}/>
            </div>
            <span className="text-xs font-bold" style={{color:"#15803d"}}>{completion}%</span>
          </div>
          <button onClick={onOpenProfile}
            className="w-full py-2 rounded-xl text-xs font-bold"
            style={{background:"#16a34a",color:"#fff"}}>
            Complete Profile →
          </button>
        </div>
      )}

      <TodaysLedger
        theme={theme} cb={cb} tc={tc} mc={mc}
        rows={[
          {label:`Active ${cl(profile,2).toLowerCase()}`,icon:"clients",iconColor:theme?.primary||"#0f766e",value:act,onClick:()=>setPage("clients")},
          {label:"Payments due",icon:"payments",iconColor:"#dc2626",value:due>0?`${currency}${dueAmount.toLocaleString("en-IN")}`:"—",valueColor:due>0?"#dc2626":undefined,onClick:()=>setPage("payments")},
          {label:`${sl(profile,2)} today`,icon:"appointments",iconColor:theme?.primary||"#0f766e",value:todApts,onClick:()=>setPage("sessions")},
          {label:"Collected this week",icon:"reports",iconColor:theme?.primary||"#0f766e",value:`${currency}${collectedWeek.toLocaleString("en-IN")}`,valueColor:collectedWeek>0?"#16a34a":undefined},
        ]}
      />

      {/* This week's birthdays highlight */}
      {birthdayToday.length>0&&(
        <div className="rounded-2xl p-4 border flex items-center gap-3" style={{background:"#fff7ed",borderColor:"#fed7aa"}}>
          <span className="text-2xl">🎂</span>
          <div className="flex-1">
            <p className="font-semibold text-sm" style={{color:tc}}>Birthday Today!</p>
            <p className="text-xs" style={{color:"#c2410c"}}>{birthdayToday.map(c=>c.name).join(", ")}</p>
          </div>
        </div>
      )}

      {/* Revenue Dashboard */}
      <div className="rounded-2xl p-5 border space-y-4" style={{background:cb,borderColor:bc}}>
        <div className="flex items-center justify-between">
          <p className="font-semibold text-sm" style={{color:tc}}>📈 Revenue</p>
          <button onClick={onOpenReports} className="text-xs font-semibold px-3 py-1.5 rounded-xl" style={{background:theme?.primary+"15",color:theme?.primary}}>Full Report →</button>
        </div>
        {(()=>{
          const currency=getCurrencySymbol(profile)||"₹";
          const thisWeekStart=new Date(); thisWeekStart.setDate(thisWeekStart.getDate()-thisWeekStart.getDay());
          const thisWeekStr=clientToday(thisWeekStart);
          const lastWeekStart=new Date(thisWeekStart); lastWeekStart.setDate(lastWeekStart.getDate()-7);
          const lastWeekStr=clientToday(lastWeekStart);
          const weekRev=payments.filter(p=>p.date>=thisWeekStr).reduce((s,p)=>s+(p.amount||0),0);
          const lastWeekRev=payments.filter(p=>p.date>=lastWeekStr&&p.date<thisWeekStr).reduce((s,p)=>s+(p.amount||0),0);
          const trend=lastWeekRev>0?Math.round(((weekRev-lastWeekRev)/lastWeekRev)*100):null;
          const topClient=[...clients].sort((a,b)=>(b.paid||0)-(a.paid||0))[0];
          const totalPending=clients.reduce((s,c)=>s+Math.max(0,(c.packagePrice||0)-(c.paid||0)),0);
          return(
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl p-3" style={{background:theme?.primary+"12"}}>
                  <p className="text-xs" style={{color:mc}}>This Week</p>
                  <p className="text-lg font-black" style={{color:theme?.primary}}>{currency}{weekRev.toLocaleString("en-IN")}</p>
                  {trend!==null&&<p className="text-xs font-semibold" style={{color:trend>=0?"#16a34a":"#ef4444"}}>{trend>=0?"↑":"↓"} {Math.abs(trend)}% vs last week</p>}
                </div>
                <div className="rounded-xl p-3" style={{background:"#fff7ed",borderColor:"#fed7aa"}}>
                  <p className="text-xs" style={{color:mc}}>This Month</p>
                  <p className="text-lg font-black" style={{color:"#c2410c"}}>{currency}{collectedMonth.toLocaleString("en-IN")}</p>
                  {totalPending>0&&<p className="text-xs" style={{color:mc}}>{currency}{totalPending.toLocaleString("en-IN")} pending</p>}
                </div>
              </div>
              {topClient&&(topClient.paid||0)>0&&(
                <div className="flex items-center gap-3 p-3 rounded-xl" style={{background:isDark?"#0f172a":"#f9fafb"}}>
                  <span className="text-xl">🏆</span>
                  <div className="flex-1">
                    <p className="text-xs font-semibold" style={{color:tc}}>Top client this year</p>
                    <p className="text-xs" style={{color:mc}}>{topClient.name} — {currency}{(topClient.paid||0).toLocaleString("en-IN")}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {(monthlyGoal>0||yearlyGoal>0)&&(
          <div className="space-y-4 pt-2 border-t" style={{borderColor:bc}}>
            <GoalBar label="Monthly Goal" collected={collectedMonth} billed={billedPending} goal={monthlyGoal} color={theme?.primary}/>
            <GoalBar label="Yearly Goal" collected={collectedYear} billed={billedPending} goal={yearlyGoal} color={theme?.primary}/>
          </div>
        )}
      </div>

      {/* Reminders Widget */}
      {(birthdays.length>0||expiring.length>0||overdue.length>0)&&(
        <div className="rounded-2xl border overflow-hidden" style={{background:cb,borderColor:bc}}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{borderColor:bc}}>
            <div className="flex items-center gap-2">
              <span className="text-base">🔔</span>
              <p className="font-semibold text-sm" style={{color:tc}}>Reminders</p>
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{background:theme?.primary+"22",color:theme?.primary}}>
                {birthdays.length+expiring.length+overdue.length}
              </span>
            </div>
            <button onClick={()=>setShowReminders(true)}
              className="text-xs font-semibold px-3 py-1.5 rounded-xl transition-all"
              style={{background:theme?.primary+"15",color:theme?.primary}}>
              See All →
            </button>
          </div>
          {/* Preview — show up to 2 items */}
          <div className="p-3 space-y-2">
            {[...birthdays.map(c=>({c,type:"birthday",label:bdayLabel(c),color:"#7c3aed"})),
              ...expiring.map(c=>({c,type:"expiring",label:`⚠️ ${c.classesRemaining} ${c.classesRemaining===1?sl(profile,1):sl(profile,2)} left`,color:"#d97706"})),
              ...overdue.map(c=>({c,type:"overdue",label:"💸 Payment overdue 30+ days",color:"#ef4444"}))
            ].slice(0,2).map(({c,type:t,label,color},i)=>(
              <div key={i} className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{background:isDark?"#0f172a":"#f8fafc"}}>
                <div className="min-w-0 flex-1 mr-2">
                  <p className="text-sm font-semibold truncate" style={{color:tc}}>{c.name}</p>
                  <p className="text-xs font-medium" style={{color}}>{label}</p>
                </div>
                {!(t==="birthday"&&bdayDaysAway(c)!==0)&&<button onClick={()=>openWa(c,t)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold flex-shrink-0"
                  style={{background:"#dcfce7",color:"#16a34a"}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="#16a34a"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </button>}
              </div>
            ))}
            {(birthdays.length+expiring.length+overdue.length)>2&&(
              <button onClick={()=>setShowReminders(true)} className="w-full text-xs py-1.5 rounded-xl font-medium" style={{color:theme?.primary,background:theme?.primary+"10"}}>
                +{(birthdays.length+expiring.length+overdue.length)-2} more reminders
              </button>
            )}
          </div>
        </div>
      )}

      {/* Full Reminders Page */}
      {showReminders&&(
        <div className="fixed inset-0 z-50 flex flex-col" style={{background:theme?.bg||"#f9fafb"}}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-4 border-b sticky top-0 z-10" style={{background:isDark?"#1e293b":"#fff",borderColor:bc}}>
            <div className="flex items-center gap-3">
              <button onClick={()=>setShowReminders(false)} className="p-1.5 rounded-xl" style={{background:isDark?"#0f172a":"#f3f4f6",color:tc}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <h2 className="font-black text-base" style={{fontFamily:"'Playfair Display',serif",color:tc}}>Reminders</h2>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex border-b px-4" style={{background:isDark?"#1e293b":"#fff",borderColor:bc}}>
            {[{id:"class",label:"📚 Classes",count:expiring.length},{id:"payment",label:"💸 Payments",count:overdue.length},{id:"birthday",label:"🎂 Birthdays",count:birthdays.length}].map(tab=>(
              <button key={tab.id} onClick={()=>setReminderTab(tab.id)}
                className="flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-all"
                style={{borderColor:reminderTab===tab.id?theme?.primary:"transparent",color:reminderTab===tab.id?theme?.primary:(isDark?"#94a3b8":"#9ca3af")}}>
                {tab.label}
                {tab.count>0&&<span className="text-xs px-1.5 py-0.5 rounded-full" style={{background:reminderTab===tab.id?theme?.primary:isDark?"#334155":"#f3f4f6",color:reminderTab===tab.id?"#fff":isDark?"#94a3b8":"#6b7280"}}>{tab.count}</span>}
              </button>
            ))}
          </div>
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {reminderTab==="class"&&(
              expiring.length===0
                ?<div className="text-center py-12 text-sm" style={{color:mc}}>No class reminders right now 🎉</div>
                :expiring.map(c=>(
                  <div key={c.id} className="rounded-2xl p-4 border flex items-center justify-between" style={{background:cb,borderColor:bc}}>
                    <div>
                      <p className="font-semibold text-sm" style={{color:tc}}>{c.name}</p>
                      <p className="text-xs mt-0.5" style={{color:"#d97706"}}>⚠️ {c.classesRemaining} {c.classesRemaining===1?sl(profile,1):sl(profile,2)} remaining</p>
                    </div>
                    <button onClick={()=>openWa(c,"expiring")}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold flex-shrink-0"
                      style={{background:"#dcfce7",color:"#16a34a"}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#16a34a"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </button>
                  </div>
                ))
            )}
            {reminderTab==="payment"&&(
              overdue.length===0
                ?<div className="text-center py-12 text-sm" style={{color:mc}}>No overdue payments 🎉</div>
                :overdue.map(c=>(
                  <div key={c.id} className="rounded-2xl p-4 border flex items-center justify-between" style={{background:cb,borderColor:bc}}>
                    <div>
                      <p className="font-semibold text-sm" style={{color:tc}}>{c.name}</p>
                      <p className="text-xs mt-0.5 text-red-500">💸 Payment overdue 30+ days</p>
                    </div>
                    <button onClick={()=>openWa(c,"overdue")}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold flex-shrink-0"
                      style={{background:"#dcfce7",color:"#16a34a"}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#16a34a"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </button>
                  </div>
                ))
            )}
            {reminderTab==="birthday"&&(
              birthdays.length===0
                ?<div className="text-center py-12 text-sm" style={{color:mc}}>No birthdays this week 🎂</div>
                :birthdays.map(c=>{
                  const isToday=bdayDaysAway(c)===0;
                  return(
                    <div key={c.id} className="rounded-2xl p-4 border flex items-center justify-between" style={{background:cb,borderColor:bc}}>
                      <div>
                        <p className="font-semibold text-sm" style={{color:tc}}>{c.name}</p>
                        <p className="text-xs mt-0.5 text-purple-500">{isToday?"🎉 Birthday today!":bdayLabel(c)}</p>
                      </div>
                      <button onClick={()=>openWa(c,"birthday")} disabled={!isToday}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold flex-shrink-0"
                        style={{background:isToday?"#dcfce7":"#f1f5f9",color:isToday?"#16a34a":"#94a3b8",opacity:isToday?1:0.6}}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="#16a34a"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        Wish
                      </button>
                    </div>
                  );
                })
            )}
          </div>
        </div>
      )}

      {/* Revenue Chart */}
      {payments.length>0&&(
        <div className="rounded-2xl p-5 border" style={{background:cb,borderColor:bc}}>
          <p className="font-semibold text-sm mb-4" style={{color:tc}}>Revenue — Last 6 Months</p>
          <div className="flex items-end gap-2 h-28">
            {rev.map((r,i)=>(
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <p className="text-xs font-semibold" style={{color:r.collected>0?theme?.primary:mc}}>
                  {r.collected>0?`₹${r.collected>=1000?Math.round(r.collected/1000)+"k":r.collected}`:""}
                </p>
                <div className="w-full rounded-t-lg transition-all" style={{
                  height:`${Math.max((r.collected/maxRev)*80,r.collected>0?8:2)}px`,
                  background:r.collected>0?theme?.primary:(isDark?"#334155":"#f3f4f6")
                }}/>
                <p className="text-xs" style={{color:mc}}>{r.label}</p>
              </div>
            ))}
          </div>
          <p className="text-xs mt-3 text-right" style={{color:mc}}>
            Total: ₹{rev.reduce((s,r)=>s+r.collected,0).toLocaleString("en-IN")}
          </p>
        </div>
      )}

      {/* Empty state — no clients yet */}
      {clients.length===0&&!payments.length&&(
        <div className="rounded-2xl p-8 border text-center" style={{background:cb,borderColor:bc}}>
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{background:theme?.light||"#ccfbf1"}}>
            <Icon name="clients" size={28} style={{color:theme?.primary}}/>
          </div>
          <h3 className="font-bold text-base mb-1" style={{color:tc}}>No {cl(profile,2).toLowerCase()} yet</h3>
          <p className="text-sm mb-4" style={{color:mc}}>Add your first client to get started tracking sessions and payments.</p>
          <button onClick={()=>setPage("clients")}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white"
            style={{background:theme?.primary||"#0f766e"}}>
            Add First {cl(profile)} →
          </button>
        </div>
      )}

      {/* Reports entry */}
      <button onClick={onOpenReports}
        className="w-full flex items-center justify-between px-5 py-4 rounded-2xl border shadow-sm transition-all"
        style={{background:cb,borderColor:bc}}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{background:theme?.light||"#ccfbf1"}}>
            <Icon name="reports" size={18} style={{color:theme?.primary}}/>
          </div>
          <div className="text-left">
            <p className="font-semibold text-sm" style={{color:tc}}>View Reports</p>
            <p className="text-xs" style={{color:mc}}>Finance · Clients · Sessions</p>
          </div>
        </div>
        <Icon name="chevron" size={16} style={{color:mc}}/>
      </button>
    </div>
  );
};
